/**
 * Creative Evaluation page — orchestrates data fetching, eval state, and
 * child components.
 *
 * Structural extractions (per PR #2906 follow-ups):
 *  - Session persistence  → useCreativeEvalSession hook
 *  - Dev tools + modals   → DevToolsPanel component (with reusable DevToolsModal)
 *  - Pure filter/query helpers → helpers.ts
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useBrands from "../../hooks/use-brands";
import { useCreativeEvalCreators } from "../../hooks/use-creative-eval-creators";
import { useCreativeEvalQuery } from "../../hooks/use-creative-eval-query";
import { useCreativeEvalLabels } from "../../hooks/use-creative-eval-labels";
import { useCreativeEvalSession } from "../../hooks/use-creative-eval-session";
import type {
  ErrorLogEntry,
  EvalQueryParams,
  FilterValues,
  HoverPreviewState,
  ImageEvaluation,
  ImageEvalState
} from "../../types/creative-eval";
import { DEBUG_QUERY_EMPTY, DEBUG_QUERY_INITIAL, DEFAULT_ACTION, DEFAULT_FILTER_VALUES } from "./constants";
import {
  buildInspectLabelsQuery,
  buildQueryParamsFromFilters,
  cloneFilters,
  defaultEvalState,
  findPreviewImage,
  getPreviewPosition,
  hasEvaluationSelections,
  queryParamsEqual
} from "./helpers";
import FilterBar from "./filter-bar";
import EvalToolbar from "./eval-toolbar";
import ImageCard from "./image-card";
import DevToolsPanel from "./dev-tools-panel";
import Lightbox from "./lightbox";
import "./creative-eval.css";

const CreativeEvalPage: React.FC = () => {
  const { brands, isLoading: isLoadingBrands } = useBrands();
  const session = useCreativeEvalSession();
  const { restored } = session;

  // ---- Filters ----
  const [panelOpen, setPanelOpen] = useState(!restored.filters);
  const [filters, setFilters] = useState<FilterValues>(() =>
    restored.filters ? cloneFilters(restored.filters) : cloneFilters(DEFAULT_FILTER_VALUES)
  );
  const { data: creators, isLoading: isLoadingCreators } = useCreativeEvalCreators(filters.brandId);

  // ---- Query ----
  const [queryParams, setQueryParams] = useState<EvalQueryParams | null>(restored.queryParams);
  const [appliedFilters, setAppliedFilters] = useState<FilterValues | null>(() =>
    restored.queryParams && restored.filters ? cloneFilters(restored.filters) : null
  );
  const {
    data,
    isLoading,
    error: queryError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useCreativeEvalQuery(queryParams, restored.initialQueryData);

  const images = useMemo(() => {
    if (!data?.pages.length) return [];
    const seenIds = new Set<number>();
    return data.pages.flatMap((page) =>
      page.images.filter((image) => {
        if (seenIds.has(image.creative_id)) return false;
        seenIds.add(image.creative_id);
        return true;
      })
    );
  }, [data]);
  const latestPage = data?.pages.at(-1);
  const latestDebugQuery = latestPage ? latestPage.debug_query?.trim() || DEBUG_QUERY_EMPTY : DEBUG_QUERY_INITIAL;

  // ---- Eval state ----
  const [evalStates, setEvalStates] = useState<Record<number, ImageEvalState>>(restored.evalStates);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(restored.hasUnsavedChanges);

  // ---- Export selection ----
  const [selectedForExport, setSelectedForExport] = useState<Set<number>>(new Set());

  // ---- Status / UI ----
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusType, setStatusType] = useState<"ok" | "err" | null>(null);
  const [debugQuery, setDebugQuery] = useState(restored.debugQuery);
  const [inspectLabelsQuery, setInspectLabelsQuery] = useState("");
  const [errorLog, setErrorLog] = useState<ErrorLogEntry[]>([]);

  // ---- Lightbox / hover preview ----
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [hoverPreview, setHoverPreview] = useState<HoverPreviewState | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);

  const { mutate: saveLabels, isPending: isSaving } = useCreativeEvalLabels();

  // ---- Callbacks ----

  const logError = useCallback((context: string, error: unknown, detail?: string) => {
    const message = error instanceof Error ? error.message : String(error);
    setErrorLog((prev) => [...prev, { time: new Date().toISOString(), context, message, detail }]);
  }, []);

  const clearPreviewFrame = useCallback(() => {
    if (rafRef.current === null) return;
    window.cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  // ---- Effects ----

  useEffect(() => {
    return clearPreviewFrame;
  }, [clearPreviewFrame]);

  // Warn before tab close when evaluations are in-progress.
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  // Persist base store when images/query change.
  useEffect(() => {
    if (!queryParams || !appliedFilters || images.length === 0) return;
    session.persistBase({
      filters: appliedFilters,
      queryParams,
      images,
      nextPageToken: latestPage?.next_page_token ?? null,
      debugQuery: latestDebugQuery
    });
  }, [appliedFilters, images, latestDebugQuery, latestPage?.next_page_token, queryParams, session]);

  // Persist draft store when eval states change (debounced inside the hook).
  useEffect(() => {
    if (!queryParams || !appliedFilters || images.length === 0) return;
    session.persistDraft({ evalStates, hasUnsavedChanges });
  }, [appliedFilters, evalStates, hasUnsavedChanges, images.length, queryParams, session]);

  // Sync debug query from latest API response.
  useEffect(() => {
    if (!latestPage) return;
    setDebugQuery(latestDebugQuery);

    if (latestPage.images.length > 0) {
      setStatusMessage(null);
      setStatusType(null);
    } else if (latestPage.count === 0) {
      setStatusMessage("No more images found. Try adjusting filters or selecting a different brand.");
      setStatusType("err");
    }
  }, [latestDebugQuery, latestPage]);

  // Surface query errors.
  useEffect(() => {
    if (queryError) {
      logError("Load Images", queryError, debugQuery);
      setStatusMessage(
        `Failed to load images: ${queryError instanceof Error ? queryError.message : String(queryError)}`
      );
      setStatusType("err");
    }
  }, [debugQuery, logError, queryError]);

  // ---- Handlers ----

  const handleFilterChange = useCallback((patch: Partial<FilterValues>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleFetch = () => {
    if (!filters.brandId) return;
    if (hasUnsavedChanges && !window.confirm("You have unsaved evaluations that will be lost.")) return;

    const params = buildQueryParamsFromFilters(filters);
    if (!params) return;

    setEvalStates({});
    setHasUnsavedChanges(false);
    setSelectedForExport(new Set());
    setStatusMessage(null);
    setStatusType(null);
    setDebugQuery(DEBUG_QUERY_INITIAL);
    setInspectLabelsQuery("");
    session.clearSession();
    setQueryParams(params);
    setAppliedFilters(cloneFilters(filters));
    setPanelOpen(false);

    if (queryParamsEqual(queryParams, params)) {
      void refetch();
    }
  };

  const handleLoadMore = () => {
    const pageToken = latestPage?.next_page_token;
    if (!filters.brandId || !queryParams || !pageToken || isFetchingNextPage) return;
    void fetchNextPage();
  };

  const scrollToTop = useCallback(() => {
    rootRef.current?.scrollTo({ top: 0, left: 0 });
    window.scrollTo({ top: 0, left: 0 });
  }, []);

  const handleOpenFilters = useCallback(() => {
    setPanelOpen(true);
    scrollToTop();
    window.requestAnimationFrame(scrollToTop);
  }, [scrollToTop]);

  const handleToggleExportSelect = useCallback((creativeId: number) => {
    setSelectedForExport((prev) => {
      const next = new Set(prev);
      if (next.has(creativeId)) next.delete(creativeId);
      else next.add(creativeId);
      return next;
    });
  }, []);

  const handleEvalChange = useCallback((creativeId: number, state: ImageEvalState) => {
    setEvalStates((prev) => {
      const next = { ...prev, [creativeId]: state };
      if (state.categories.size === 0 && Object.keys(state.details).length === 0 && !state.severity && !state.notes) {
        delete next[creativeId];
      }
      setHasUnsavedChanges(hasEvaluationSelections(next));
      return next;
    });
  }, []);

  const handleSave = () => {
    if (!hasUnsavedChanges) return;

    const imageMap = new Map(images.map((image) => [image.creative_id, image]));
    const evaluations: Record<string, ImageEvaluation> = {};

    for (const [idStr, state] of Object.entries(evalStates)) {
      if (state.categories.size === 0) continue;
      const img = imageMap.get(Number(idStr));
      const categories: Record<string, { detail: string | null }> = {};
      for (const cat of state.categories) {
        categories[cat] = { detail: state.details[cat] || null };
      }
      evaluations[idStr] = {
        brand_id: img?.brand_id || filters.brandId,
        categories,
        severity: state.severity,
        notes: state.notes
      };
    }
    if (Object.keys(evaluations).length === 0) return;
    const savedIds = Object.keys(evaluations).map(Number);
    setInspectLabelsQuery("");
    saveLabels(
      { evaluations },
      {
        onSuccess: (response) => {
          setStatusMessage(`Saved ${response.saved} labels for ${response.images_evaluated} images`);
          setStatusType("ok");
          setInspectLabelsQuery(buildInspectLabelsQuery(savedIds));
          setHasUnsavedChanges(false);
        },
        onError: (error) => {
          logError("Save Results", error, `${Object.keys(evaluations).length} evaluations`);
          setStatusMessage(`Save failed: ${error instanceof Error ? error.message : String(error)}`);
          setStatusType("err");
        }
      }
    );
  };

  const labeledCount = Object.values(evalStates).filter((s) => s.categories.size > 0).length;

  const handleActionToggle = (action: string) => {
    setFilters((prev) => {
      if (prev.selectedActions.includes(action)) {
        const next = prev.selectedActions.filter((a) => a !== action);
        return { ...prev, selectedActions: next.length ? next : [DEFAULT_ACTION] };
      }
      return { ...prev, selectedActions: [...prev.selectedActions, action] };
    });
  };

  const handlePreviewPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const { target, clientX, clientY } = event;
      clearPreviewFrame();

      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        const image = findPreviewImage(target);

        if (!image) {
          setHoverPreview(null);
          return;
        }

        setHoverPreview({ src: image.src, ...getPreviewPosition(clientX, clientY) });
      });
    },
    [clearPreviewFrame]
  );

  const handlePreviewPointerLeave = useCallback(() => {
    clearPreviewFrame();
    setHoverPreview(null);
  }, [clearPreviewFrame]);

  // ---- Render ----

  return (
    <div
      ref={rootRef}
      className="ce-root"
      onPointerMove={handlePreviewPointerMove}
      onPointerLeave={handlePreviewPointerLeave}
    >
      <div className="header">
        <div className="header-brand">
          <span>Creative</span> <em>Evaluation</em>
        </div>
        <div className="header-right">
          <DevToolsPanel
            debugQuery={debugQuery}
            inspectLabelsQuery={inspectLabelsQuery}
            errorLog={errorLog}
            images={images}
            selectedForExport={selectedForExport}
          />
        </div>
      </div>

      <div className="container">
        <EvalToolbar
          totalImages={images.length}
          labeledCount={labeledCount}
          hasUnsavedChanges={hasUnsavedChanges}
          isSaving={isSaving}
          isLoadingMore={isFetchingNextPage}
          hasMore={images.length > 0 && !!hasNextPage}
          hasStarted={queryParams !== null}
          isFiltersOpen={panelOpen}
          statusMessage={statusMessage}
          statusType={statusType}
          onSave={handleSave}
          onLoadMore={handleLoadMore}
          onNewEval={handleOpenFilters}
        />

        <FilterBar
          open={panelOpen}
          brands={brands}
          isLoadingBrands={isLoadingBrands}
          creators={creators}
          isLoadingCreators={isLoadingCreators}
          filters={filters}
          isLoading={isLoading}
          onFilterChange={handleFilterChange}
          onActionToggle={handleActionToggle}
          onFetch={handleFetch}
          onClose={() => setPanelOpen(false)}
        />

        {images.length > 0 && (
          <div className="images-grid">
            {images.map((image) => (
              <ImageCard
                key={image.creative_id}
                image={image}
                evalState={evalStates[image.creative_id] || defaultEvalState()}
                isSelectedForExport={selectedForExport.has(image.creative_id)}
                onEvalChange={(state) => handleEvalChange(image.creative_id, state)}
                onToggleExportSelect={() => handleToggleExportSelect(image.creative_id)}
                onLightbox={setLightboxUrl}
              />
            ))}
          </div>
        )}

        {isLoading && images.length === 0 && (
          <div className="loading">
            <div className="loading-spinner" />
            <div>Fetching images...</div>
          </div>
        )}

        {!isLoading && images.length === 0 && queryParams && (
          <div className="no-more">No more images found. Try adjusting filters or selecting a different brand.</div>
        )}
      </div>

      {hoverPreview && !lightboxUrl && (
        <div className="hover-preview visible" style={{ left: hoverPreview.x, top: hoverPreview.y }}>
          <img src={hoverPreview.src} alt="Hover preview" />
        </div>
      )}

      {lightboxUrl && <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
    </div>
  );
};

export default CreativeEvalPage;
